import { useEffect, useRef, useState } from "react";
import { useInterval } from "react-use";
import { useTranslation } from "../../lib";
import { useSelectTheme } from "../../store";
import { Button, ButtonText } from "..";

export interface IdleSignOutWarningPopPropsT {
  /** When the sign-out happens unless the user answers. */
  signOutAt: number;
  /** "Stay signed in" is waiting for the server, so a second press sends nothing more. */
  stayPending?: boolean;
  onStaySignedIn: () => void;
  onSignOut: () => void;
}

// minutes and seconds left, as the countdowns before a sign-out show them
export const formatSignOutCountdown = (ms: number) => {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(totalSeconds / 60)}:${String(totalSeconds % 60).padStart(2, "0")}`;
};

/**
 * The warning shown before an idle sign-out. "Stay signed in" takes focus, so one key press
 * answers it: WCAG 2.2 SC 2.2.1 asks for a simple way to extend a time limit the user cannot
 * turn off.
 */
export const IdleSignOutWarningPop = (props: IdleSignOutWarningPopPropsT) => {
  const { signOutAt, stayPending = false, onStaySignedIn, onSignOut } = props;
  const { t } = useTranslation();
  const { color, pad, fontSize, fontWeight, borderRadius } = useSelectTheme();

  const [nowMs, setNowMs] = useState(() => Date.now());
  useInterval(() => setNowMs(Date.now()), 1000);

  // Escape dismisses a dialog, and dismissing this one means the user is here. Not `useHandleEsc`:
  // the popups beneath close on the same bubbling key and lose what was typed in them, so this
  // listens in the capture phase and stops the key there.
  const onStaySignedInRef = useRef(onStaySignedIn);
  onStaySignedInRef.current = onStaySignedIn;
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" && event.keyCode !== 27) return;
      event.stopPropagation();
      onStaySignedInRef.current();
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, []);

  return (
    // Above every layer in the app, the confirm popups' 99999 included: it mounts outside the
    // router, and full-screen pages and popups would otherwise cover a warning that ignores input.
    <div
      data-testid="idle_sign_out_backdrop"
      css={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        zIndex: 100000,
        background: "#00000040",
      }}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="idle_sign_out_title"
        aria-describedby="idle_sign_out_message"
        css={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          boxSizing: "border-box",
          width: "480px",
          maxWidth: "calc(100vw - 32px)",
          padding: pad.xl,
          borderRadius: borderRadius.default,
          background: color.white,
          color: color.textIcon.title,
        }}
      >
        <h2
          id="idle_sign_out_title"
          css={{ margin: 0, fontSize: fontSize.h3, fontWeight: fontWeight.bold }}
        >
          {t("idle_sign_out.warning_title")}
        </h2>
        <p id="idle_sign_out_message" css={{ margin: `${pad.large} 0 ${pad.mediumSmall}` }}>
          {t("idle_sign_out.warning_message")}
        </p>
        {/* Not a live region: a number that changes every second would be read out every second. */}
        <p
          data-testid="idle_sign_out_countdown"
          css={{
            margin: `0 0 ${pad.xl}`,
            fontWeight: fontWeight.bold,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {t("idle_sign_out.countdown", { time: formatSignOutCountdown(signOutAt - nowMs) })}
        </p>
        <div
          css={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: pad.small,
          }}
        >
          <ButtonText onClick={onSignOut}>{t("idle_sign_out.sign_out_button")}</ButtonText>
          <Button type="button" autoFocus disabled={stayPending} onClick={onStaySignedIn}>
            {t("idle_sign_out.stay_signed_in_button")}
          </Button>
        </div>
      </div>
    </div>
  );
};
